import {
    AccruedBillingNoun,
    buildAccruedBillingViolationMessage,
    findAccruedBillingViolation,
} from "@common/helpers/domains/accounting/accruedBillingRule";
import {
    ScheduleSubModes,
    isAccruedScheduleMode,
} from "@common/helpers/domains/accounting/accruedScheduleMode";
import { getAccruedDefaultTermStart } from "@common/helpers/domains/accounting/accruedTermStart";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";

type TermStartTemplate = Pick<
    ScheduleTemplateResponse,
    "termLength" | "termUnit"
> &
    Partial<ScheduleSubModes>;

/** The invoice, bill or payment billing the amount, checked against an accrued ladder. */
export interface ScheduleAccruedBilling {
    billedDateOnly: string;
    amount: number;
    noun: AccruedBillingNoun;
}

/**
 * The term start the apply form pre-fills for an invoice: an accrued policy
 * runs back from the invoice month, any other starts on the invoice date.
 */
export function getScheduleApplyTermStart(options: {
    template: TermStartTemplate | undefined;
    sourceDate: string;
}): string {
    const { template, sourceDate } = options;
    if (!template || !isAccruedScheduleMode(template)) {
        return sourceDate;
    }
    return getAccruedDefaultTermStart({
        sourceDate,
        termLength: template.termLength,
        termUnit: template.termUnit,
    });
}

/** The billing an accrued ladder is checked against, or null when the source has no date. */
export function buildAccruedBilling(options: {
    sourceDate: string | null;
    amount: number;
    noun: AccruedBillingNoun;
}): ScheduleAccruedBilling | null {
    return options.sourceDate
        ? {
              billedDateOnly: options.sourceDate,
              amount: options.amount,
              noun: options.noun,
          }
        : null;
}

/** The accrued billing rule on the apply preview, worded as the server refuses it. */
export function findAccruedPreviewError(options: {
    template:
        | (Pick<ScheduleTemplateResponse, "type"> & Partial<ScheduleSubModes>)
        | undefined;
    billing: ScheduleAccruedBilling;
    rows: Array<{ effectiveDate: string; amount: number }>;
}): string | null {
    const { template } = options;
    if (!template || !isAccruedScheduleMode(template)) {
        return null;
    }
    const violation = findAccruedBillingViolation({
        periods: options.rows,
        billings: [options.billing],
    });
    return violation
        ? buildAccruedBillingViolationMessage({
              noun: options.billing.noun,
              violation,
              scheduleType: template.type,
          })
        : null;
}
